<template>
  <div>
    <div class="page-toolbar">
      <div class="publication-title-wrap">
        <el-button size="mini" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <div>
          <div class="page-title">{{ pageTitle }}</div>
          <div class="page-subtitle">用户ID {{ userCode }} · 共 {{ publicationCount }} 条</div>
        </div>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading || userLoading" @click="refreshAll">刷新</el-button>
    </div>

    <el-card class="page-card">
      <div class="search-toolbar">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          placeholder="按ID/标题/品种/正文查询"
          style="width: 260px;"
          @keyup.enter.native="loadProducts"
        />
        <el-select v-model="filters.type" placeholder="发布类型" style="width: 150px;">
          <el-option label="全部类型" value="" />
          <el-option label="销售" value="SALE" />
          <el-option label="求购" value="PURCHASE" />
        </el-select>
        <el-select v-model="filters.auditStatus" placeholder="审批状态" style="width: 150px;">
          <el-option label="全部审批状态" value="" />
          <el-option label="待审批" value="PENDING" />
          <el-option label="已通过" value="APPROVED" />
          <el-option label="已驳回" value="REJECTED" />
        </el-select>
        <el-select v-model="filters.onShelf" placeholder="上架状态" style="width: 150px;">
          <el-option label="全部上架状态" value="" />
          <el-option label="上架中" :value="true" />
          <el-option label="已下架" :value="false" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="loadProducts">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>

      <el-table :data="products" v-loading="loading" stripe>
        <el-table-column type="index" width="56" label="#" />
        <el-table-column label="类型" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="productTypeTag(row)">{{ productTypeLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="记录ID" min-width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatDisplayProductId(row.id) }}</template>
        </el-table-column>
        <el-table-column prop="name" label="标题" min-width="180" show-overflow-tooltip />
        <el-table-column prop="category" label="品种" width="110" />
        <el-table-column label="价格（元/吨）" width="140">
          <template slot-scope="{ row }">
            <span v-if="formatPrice(row.price)">
              <span>{{ formatPrice(row.price) }}</span><span class="price-unit">/吨</span>
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="数量（吨）" width="120">
          <template slot-scope="{ row }">{{ formatQuantity(row.stock) }}</template>
        </el-table-column>
        <el-table-column label="审批状态" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="auditTagType(row)">{{ auditLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上架状态" width="100">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.onShelf ? 'success' : 'info'">
              {{ row.onShelf ? '上架中' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="发布时间" width="170" />
        <el-table-column label="操作" width="350" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="showDetail(row)">查看</el-button>
            <el-button
              v-if="isPending(row) || isRejected(row)"
              size="mini"
              type="success"
              @click="approveProduct(row)"
            >
              审核通过
            </el-button>
            <el-button
              v-if="isPending(row) || isApproved(row)"
              size="mini"
              type="danger"
              @click="rejectProduct(row)"
            >
              驳回
            </el-button>
            <el-button
              v-if="isApproved(row)"
              size="mini"
              :type="row.onShelf ? 'warning' : 'primary'"
              @click="toggleShelf(row)"
            >
              {{ row.onShelf ? '下架' : '上架' }}
            </el-button>
            <el-button
              size="mini"
              type="danger"
              :loading="deletingId === row.id"
              @click="deleteProduct(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="current ? `${productEntityName(current)}详情` : '发布详情'"
      :visible.sync="detailVisible"
      width="780px"
    >
      <div v-if="current">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="发布类型">{{ productTypeLabel(current) }}</el-descriptions-item>
          <el-descriptions-item :label="`${productEntityName(current)}ID`">{{ formatDisplayProductId(current.id) }}</el-descriptions-item>
          <el-descriptions-item label="标题" :span="2">{{ current.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品种">{{ current.category || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchaseProduct(current)" label="规格">{{ resolveSpecification(current) }}</el-descriptions-item>
          <el-descriptions-item :label="priceLabel(current)">
            <span v-if="formatPrice(current.price)">
              <span>{{ formatPrice(current.price) }}</span><span class="price-unit">/吨</span>
            </span>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item :label="quantityLabel(current)">{{ formatQuantity(current.stock) }}</el-descriptions-item>
          <el-descriptions-item v-if="isPurchaseProduct(current)" label="正文" :span="2">{{ current.description || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchaseProduct(current)" label="货物所在地">{{ current.region || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchaseProduct(current)" label="产地">{{ current.productionPlace || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="!isPurchaseProduct(current)" label="生产日期">{{ current.productionDate || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发布人名称/昵称">{{ current.supplierDisplayName || current.supplierName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="公司">{{ current.supplierCompanyName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="审批状态">{{ auditLabel(current) }}</el-descriptions-item>
          <el-descriptions-item label="上架状态">{{ current.onShelf ? '上架中' : '已下架' }}</el-descriptions-item>
          <el-descriptions-item label="驳回/审批说明" :span="2">{{ current.auditComment || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ current.createTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="展示图片" :span="2">
            <div v-if="resolveDetailImages(current).length" class="detail-images">
              <el-image
                v-for="(image, index) in resolveDetailImages(current)"
                :key="`${index}-${image}`"
                :src="image"
                :preview-src-list="resolveDetailImages(current)"
                fit="cover"
                class="detail-image-item"
              />
            </div>
            <span v-else>-</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchProducts, fetchUserDetail } from '../../api/admin'
import productManagementMixin from '../../utils/product-management'

export default {
  name: 'UserPublicationsView',
  mixins: [productManagementMixin],
  data() {
    return {
      loading: false,
      userLoading: false,
      user: null,
      products: [],
      filters: {
        keyword: '',
        type: '',
        auditStatus: '',
        onShelf: ''
      }
    }
  },
  computed: {
    openid() {
      return String((this.$route.params && this.$route.params.openid) || '').trim()
    },
    pageTitle() {
      return `${this.formatDisplayName(this.user)}的发布`
    },
    userCode() {
      return this.formatUserCode(this.user)
    },
    publicationCount() {
      const count = Number(this.user && this.user.publicationCount)
      return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
    }
  },
  watch: {
    '$route.params.openid'() {
      this.resetPageState()
      this.refreshAll()
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      await Promise.all([this.loadUser(), this.loadProducts()])
    },
    async loadUser() {
      if (!this.openid) {
        this.$message.error('用户信息无效')
        return
      }
      this.userLoading = true
      try {
        this.user = await fetchUserDetail(this.openid)
      } catch (error) {
        this.$message.error(error.message || '用户信息获取失败')
      } finally {
        this.userLoading = false
      }
    },
    async loadProducts() {
      if (!this.openid) return
      this.loading = true
      try {
        const params = { supplierId: this.openid }
        const keyword = String(this.filters.keyword || '').trim()
        if (keyword) params.keyword = keyword
        if (this.filters.type) params.type = this.filters.type
        if (this.filters.auditStatus) params.auditStatus = this.filters.auditStatus
        if (this.filters.onShelf !== '') params.onShelf = this.filters.onShelf
        const products = await fetchProducts(params)
        this.products = Array.isArray(products) ? products : []
      } catch (error) {
        this.$message.error(error.message || '用户发布列表获取失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { keyword: '', type: '', auditStatus: '', onShelf: '' }
      this.loadProducts()
    },
    resetPageState() {
      this.user = null
      this.products = []
      this.current = null
      this.detailVisible = false
      this.filters = { keyword: '', type: '', auditStatus: '', onShelf: '' }
    },
    goBack() {
      this.$router.push({ name: 'users' })
    },
    formatDisplayName(user = {}) {
      const nickName = String((user && (user.nickName || user.nickname)) || '').trim()
      if (nickName) return nickName
      const contactName = String((user && user.contactName) || '').trim()
      if (contactName) return contactName
      return this.formatUserCode(user)
    },
    formatUserCode(user = {}) {
      const directCode = String((user && user.userCode) || '').trim()
      if (directCode) return directCode
      const userId = Number(user && user.userId)
      if (!Number.isInteger(userId) || userId <= 0) return '--'
      return userId < 1000 ? String(userId).padStart(3, '0') : String(userId)
    },
    priceLabel(row = {}) {
      return this.isPurchaseProduct(row) ? '期望价格（元/吨）' : '价格（元/吨）'
    },
    quantityLabel(row = {}) {
      return this.isPurchaseProduct(row) ? '求购数量（吨）' : '库存（吨）'
    },
    async onProductDeleted() {
      await Promise.all([this.loadUser(), this.loadProducts()])
    }
  }
}
</script>

<style scoped>
.publication-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-subtitle {
  margin-top: 4px;
  color: #7d88a3;
  font-size: 12px;
}

.search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.price-unit {
  margin-left: 1px;
  font-size: 0.92em;
  color: #909399;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-image-item {
  width: 100%;
  height: 110px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
</style>
